import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Formats a "YYYY-MM-DD" calendar date as e.g. "Thu, Apr 30, 2026".
// Parses as a plain UTC calendar date (Date.UTC) then formats with
// timeZone:'UTC' — the same pattern as getFullDayName below — so the
// weekday/date never shifts by a day for browsers west of UTC. Plain
// `new Date(dateString).toLocaleDateString(...)` (the old implementation)
// parsed as UTC midnight but read back in browser-LOCAL time, which is
// exactly that bug.
export function formatDate(dateString) {
  if (!dateString) return '';
  const [y, m, d] = String(dateString).split('-').map(Number);
  if (!y || !m || !d) return String(dateString);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(dt);
}

export function formatTime(timeString) {
  return timeString;
}

/**
 * Compute the end time for a booking's time slot given a start slot
 * (e.g. "10:00-11:00") and duration in hours.
 * Returns "HH:MM" — wraps to 00:00 once the day rolls over.
 */
export function getEndTime(startSlot, duration = 1) {
  if (!startSlot) return '';
  const startTime = String(startSlot).split('-')[0];
  const [hoursRaw, minutesRaw] = startTime.split(':').map(Number);
  if (Number.isNaN(hoursRaw) || Number.isNaN(minutesRaw)) return '';
  let endHour = hoursRaw + Number(duration || 1);
  if (endHour >= 24) endHour -= 24;
  return `${String(endHour).padStart(2, '0')}:${String(minutesRaw).padStart(2, '0')}`;
}

/**
 * Canonical booking time-range formatter used across customer + admin views.
 *
 *   1-hour booking  →  "15:00-16:00"
 *   3-hour booking  →  "15:00-18:00 (3h)"
 *
 * Always uses a tight "HH:MM-HH:MM" range (no spaces around the dash) and
 * appends a "(Xh)" suffix when duration > 1 so multi-hour bookings are
 * unambiguous in receipts, confirmations, lists and admin cards.
 */
export function formatBookingTimeRange(timeSlot, duration = 1) {
  if (!timeSlot) return '';
  const hrs = Number(duration || 1);
  const start = String(timeSlot).split('-')[0];
  const end = getEndTime(timeSlot, hrs);
  if (!end) return timeSlot;
  const range = `${start}-${end}`;
  return hrs > 1 ? `${range} (${hrs}h)` : range;
}

export function getStatusColor(status) {
  // Maps booking statuses to theme.css `.badge-*` classes (defined in
  // /app/frontend/src/styles/theme.css). Always pair with `.badge`:
  //   <span className={`badge ${getStatusColor(status)}`}>
  const colors = {
    AWAITING_PAYMENT: 'badge-awaiting',
    CONFIRMED:        'badge-confirmed',
    CANCELLED:        'badge-cancelled',
    EXPIRED:          'badge-expired',
    PENDING_ALLOCATION: 'badge-awaiting',
  };
  return colors[status] || 'badge-expired';
}

export function getSportColor(sport) {
  return 'badge-basketball';
}

const FULL_DAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

// Full, capitalized weekday name for a "YYYY-MM-DD" calendar date — used by
// the "Date: <Weekday>" label on both the landing page's Check Availability
// card and the booking flow's date step. Parses the date as a plain UTC
// calendar date (Date.UTC + getUTCDay) rather than `new Date(dateStr)`,
// which is parsed as UTC midnight but read back via LOCAL getDay() — that
// shifts the weekday by one day for any browser west of UTC. This must
// always match the actual selected calendar date regardless of the
// visitor's local timezone.
export function getFullDayName(dateStr) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(Date.UTC(y, m - 1, d));
  return FULL_DAY_NAMES[dt.getUTCDay()];
}